import { ITEM_CONDITIONS, publicFields, type Item } from "./types.js";
import { CURRENCY_LABELS, priceFromCents, type Currency } from "./validate.js";

/**
 * Stage 3, the way out: an item (or several) turned into a prompt somebody
 * pastes into a chat with a language model of their choice.
 *
 * **This is the first place private data could leave the app**, so the whole
 * module is arranged around one rule: what goes into a prompt is decided here,
 * by `promptItem()`, and by nothing else. Fields go through `publicFields()`;
 * `notes` and private fields are added only when the caller passes
 * `includePrivate`, and then under names ("notes", "privateFields") that say
 * what they are. The rest of the item -- id, title, description, condition,
 * price, category, tags, public fields -- is public by decision.
 *
 * It is deliberately independent of the importer (`enrichImport.ts`): the
 * response schema is described in words below and enforced there, and neither
 * imports the other. A direct API integration replaces this pair with one call
 * and a review screen that already exists.
 */

/** More than this makes a prompt nobody wants to paste and a reply a model tends to cut short. */
export const MAX_ENRICH_ITEMS = 10;

export interface PromptEntry {
  item: Item;
  /** The category's name, or null when the item has none. The id means nothing to a model. */
  categoryName: string | null;
}

export interface PromptOptions {
  /**
   * Off unless somebody turns it on. Includes `notes` and every private field,
   * which is exactly the material the owner marked as not for anyone else.
   */
  includePrivate: boolean;
  /** Names already in use, so the model reuses "Power tools" instead of inventing "Electric drills". */
  categoryNames: readonly string[];
  /**
   * The inventory's currency. The schema's key stays `priceDollars`, which
   * the import reads, so anything but dollars is said in a line of its own.
   */
  currency?: Currency;
  /**
   * Photos travel with the prompt, each set introduced by its item's id. Only
   * a direct call can attach them; the copied prompt leaves this off and the
   * person pastes photos into their chat by hand.
   */
  photosAttached?: boolean;
}

/** Which of the built-in details are still empty, in the words the schema uses. */
export function missingDetails(item: Item, categoryName: string | null): string[] {
  const missing: string[] = [];
  if (!item.title.trim()) missing.push("title");
  if (!item.description.trim()) missing.push("description");
  if (!item.condition) missing.push("condition");
  if (!categoryName) missing.push("category");
  if (item.priceCents === null) missing.push("priceDollars");
  if (item.tags.length === 0) missing.push("tags");
  return missing;
}

/**
 * One item as the model sees it. Exported so a test can check the boundary on
 * the object itself, not only on the text built from it.
 */
export function promptItem(
  { item, categoryName }: PromptEntry,
  includePrivate: boolean,
): Record<string, unknown> {
  const shown: Record<string, unknown> = {
    id: item.id,
    title: item.title,
    description: item.description,
    condition: item.condition,
    category: categoryName ?? "",
    priceDollars: item.priceCents === null ? null : Number(priceFromCents(item.priceCents)),
    tags: item.tags,
    fields: publicFields(item.fields).map(({ label, value }) => ({ label, value })),
    missing: missingDetails(item, categoryName),
  };

  if (includePrivate) {
    shown["notes"] = item.notes;
    shown["privateFields"] = item.fields
      .filter((field) => field.visibility === "private")
      .map(({ label, value }) => ({ label, value }));
  }

  return shown;
}

const SCHEMA = `{
  "items": [
    {
      "id": "<the id exactly as given>",
      "title": "<short, specific: brand, model, what it is>",
      "description": "<one or two plain sentences a buyer or a future you would want>",
      "condition": "<one of: ${ITEM_CONDITIONS.filter(Boolean).join(", ")}>",
      "category": "<one short name>",
      "priceDollars": <a number, a fair used price>,
      "tags": ["<a few short words>"],
      "fields": [{ "label": "Brand", "value": "DeWalt" }]
    }
  ]
}`;

export function buildEnrichPrompt(entries: readonly PromptEntry[], options: PromptOptions): string {
  const many = entries.length > 1;
  const items = entries.slice(0, MAX_ENRICH_ITEMS).map((entry) => promptItem(entry, options.includePrivate));

  const lines = [
    `I keep an inventory of the things I own. Below ${many ? "are items" : "is an item"} with whatever I have written down so far. Fill in what is missing so I can find ${many ? "them" : "it"} again and, later, sell ${many ? "them" : "it"}.`,
    "",
    "How to answer:",
    "- Work on each item's `missing` list first. Suggest a change to something already filled in only if it is clearly wrong.",
    "- Never guess what cannot be known from the description: serial numbers, purchase dates, dimensions you were not given. Leave that key out instead.",
    "- Leave out any key you have nothing good for. An empty answer beats a made-up one.",
    "- Extra facts worth keeping (brand, model, colour, material, size) go in `fields` as label and value.",
    ...(options.photosAttached
      ? [
          "- Photos of some items come before this text, each set introduced by that item's id. Use them: a label, nameplate or box in a photo is good evidence for brand, model and condition. Do not read out serial numbers from them.",
        ]
      : []),
    "- Keep each item's `id` exactly as given so I can match your answer back.",
    ...(options.currency && options.currency !== "USD"
      ? [
          `- Prices are in ${CURRENCY_LABELS[options.currency]}s (${options.currency}). Give \`priceDollars\` in ${options.currency} too; the key keeps its name.`,
        ]
      : []),
    ...(options.categoryNames.length > 0
      ? [`- Prefer one of my existing categories if it fits: ${options.categoryNames.join(", ")}.`]
      : []),
    ...(options.includePrivate
      ? [
          "- `notes` and `privateFields` are private to me: use them to understand the item, but never repeat them in `description`.",
        ]
      : []),
    "",
    'Reply with only JSON in exactly this shape (a "json" code block is fine):',
    "",
    SCHEMA,
    "",
    "The item" + (many ? "s" : "") + ":",
    "",
    JSON.stringify({ items }, null, 2),
  ];

  return lines.join("\n");
}
